import { pointFrom } from "@excalidraw/math";

import type { ExcalidrawElementSkeleton } from "@excalidraw/element/transform";
import type { LocalPoint } from "@excalidraw/math";

import { SHAPE_STROKE_WIDTH } from "./strokeConstants";

// Lines need a non-zero width AND height, otherwise convertToExcalidrawElements
// falls back to a default 100×0 bounding box (see
// packages/element/src/transform.ts:550) which collapses on group resize.
// 0.01px is visually imperceptible at any zoom.
export const LINE_EPSILON = 0.01;

/**
 * Build a single line element going from (ox, oy) by (dx, dy).
 * Replaces zero deltas with LINE_EPSILON so the bounding box stays consistent
 * with the point geometry under group resize.
 */
export const safeLine = (
  ox: number,
  oy: number,
  dx: number,
  dy: number,
  groupId: string,
): ExcalidrawElementSkeleton => {
  const safeDx = dx === 0 ? LINE_EPSILON : dx;
  const safeDy = dy === 0 ? LINE_EPSILON : dy;
  return {
    type: "line",
    x: ox,
    y: oy,
    width: Math.abs(safeDx),
    height: Math.abs(safeDy),
    points: [
      pointFrom<LocalPoint>(0, 0),
      pointFrom<LocalPoint>(safeDx, safeDy),
    ],
    strokeWidth: SHAPE_STROKE_WIDTH,
    groupIds: [groupId],
  };
};

/**
 * Build a closed polygon as a single multi-point line element. Vertices are in
 * shape-local coordinates; this helper computes the bounding box, shifts the
 * points to be relative to it, and appends the first vertex if the caller
 * didn't already close the path.
 *
 * Pass `smooth: true` to render the path with curve interpolation through the
 * vertices instead of straight segments — useful for pill / capsule shapes
 * where the polygon would otherwise show visible kinks.
 */
export const polygon = (
  vertices: ReadonlyArray<readonly [number, number]>,
  groupId: string,
  ox: number,
  oy: number,
  options: { smooth?: boolean } = {},
): ExcalidrawElementSkeleton => {
  const xs = vertices.map(([x]) => x);
  const ys = vertices.map(([, y]) => y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  const width = Math.max(maxX - minX, LINE_EPSILON);
  const height = Math.max(maxY - minY, LINE_EPSILON);

  const first = vertices[0];
  const last = vertices[vertices.length - 1];
  const closed: ReadonlyArray<readonly [number, number]> =
    first[0] === last[0] && first[1] === last[1]
      ? vertices
      : [...vertices, first];

  return {
    type: "line",
    x: ox + minX,
    y: oy + minY,
    width,
    height,
    points: closed.map(([x, y]) =>
      pointFrom<LocalPoint>(x - minX, y - minY),
    ),
    // type: 2 = ROUNDNESS.PROPORTIONAL_RADIUS, which on linear elements
    // routes rendering through rough.js's `generator.curve()` for smooth
    // bezier interpolation through the points.
    roundness: options.smooth ? { type: 2 } : null,
    strokeWidth: SHAPE_STROKE_WIDTH,
    groupIds: [groupId],
  };
};
